import { CanonStatus, InboxDismissalScope } from '../domain/enums.js';
import { InboxCountsEvent } from '../events/app-events.js';
import { InboxCaptureText, InboxCategoryKey, InboxItemV1 } from '../inbox/categories.js';
import { z } from '../zod.js';
import { IsoTimestamp, JsonValue, RowVersion, Uuid } from './common.js';
import { DocTitle, TypeKey } from './documents.js';
import { DocRefV1 } from './links.js';
import { Cursor, listOf, PageQueryV1 } from './list.js';
import { MentionLinkRequestV1, UnlinkedOccurrenceV1 } from './search.js';
/**
 * The catalogue (1.U), the Inbox and the hygiene actions (1.N-3) of exit 1b (1.0b deliverable
 * 20): a type's documents as a filtered table, the Inbox's items, counts, dismissals and
 * captures, merge and Link all.
 */
const Count = z.number().int().nonnegative();
// -------------------------------------------------------------------------------- catalogue
/** `/api/catalogue/:typeKey/query`: a canon or a reference type. */
export const CatalogueParams = z.strictObject({ typeKey: TypeKey });
/**
 * A catalogue column: a field's key, or one of the document columns `title`, `canonStatus`,
 * `playState`, `discovery`, `updatedAt`.
 */
export const CatalogueColumn = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/);
/** How a filter compares a column with its value; `empty` and `notEmpty` take no value. */
export const CATALOGUE_OPERATORS = [
    'eq',
    'neq',
    'lt',
    'lte',
    'gt',
    'gte',
    'contains',
    'in',
    'empty',
    'notEmpty',
];
export const CatalogueOperator = z.enum(CATALOGUE_OPERATORS);
export const CatalogueFilterV1 = z
    .strictObject({ column: CatalogueColumn, op: CatalogueOperator, value: JsonValue.optional() })
    .refine((f) => (f.op === 'empty' || f.op === 'notEmpty') === (f.value === undefined), {
    message: 'every operator but empty and notEmpty takes a value',
    path: ['value'],
});
/**
 * `catalogue.query`: text, filters (all must hold) and sort keys over one type's documents, with
 * temporal fields read at NOW; paged by `cursor` and `limit` (1 to 200, default 50).
 */
export const CatalogueQueryV1 = z.strictObject({
    q: z.string().max(200).optional(),
    filters: z.array(CatalogueFilterV1).max(20).default([]),
    sort: z
        .array(z.strictObject({ column: CatalogueColumn, direction: z.enum(['asc', 'desc']) }))
        .max(5)
        .default([]),
    cursor: Cursor.optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
/** The document columns a catalogue filters and sorts besides the type's fields (1.U deliverable 1). */
export const CATALOGUE_DOCUMENT_COLUMNS = [
    'title',
    'canonStatus',
    'playState',
    'discovery',
    'updatedAt',
];
const NUMBER_OPERATORS = [
    'eq',
    'neq',
    'lt',
    'lte',
    'gt',
    'gte',
    'in',
    'empty',
    'notEmpty',
];
const TEXT_OPERATORS = ['eq', 'neq', 'in', 'contains', 'empty', 'notEmpty'];
/**
 * The operators each column takes (CS r2c; 1.U deliverable 2's table, DECISIONS 141 item 3), by
 * field kind and by document column; a pair outside it answers `bad_operator`. `world_date` takes
 * neither `in` nor `contains` (1.U deliverable 11; `not_filterable` until 1.U stage s2), `asset`
 * only `empty` and `notEmpty`, `updatedAt` only the four comparisons. 1.U's planner and 1.N-2's
 * filter menus read this one table.
 */
export const CATALOGUE_KIND_OPERATORS = {
    text: TEXT_OPERATORS,
    long_text: TEXT_OPERATORS,
    url: TEXT_OPERATORS,
    number: NUMBER_OPERATORS,
    dice: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in', 'contains', 'empty', 'notEmpty'],
    boolean: ['eq', 'neq', 'empty', 'notEmpty'],
    select: NUMBER_OPERATORS,
    multi_select: NUMBER_OPERATORS,
    real_date: NUMBER_OPERATORS,
    asset: ['empty', 'notEmpty'],
    doc_ref: TEXT_OPERATORS,
    doc_refs: TEXT_OPERATORS,
    world_date: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'empty', 'notEmpty'],
    title: ['eq', 'neq', 'in', 'contains'],
    canonStatus: ['eq', 'neq', 'in'],
    playState: ['eq', 'neq', 'in', 'empty', 'notEmpty'],
    discovery: ['eq', 'neq', 'in', 'empty', 'notEmpty'],
    updatedAt: ['lt', 'lte', 'gt', 'gte'],
};
/**
 * Why a catalogue query is refused (CS r2c; 1.U ruling (d), DECISIONS 141 item 2, 144 item 8): 400
 * `invalid_request` with `details.issue` one of these. 1.N-2 maps each to its notice.
 */
export const CATALOGUE_ISSUE_CODES = [
    'unknown_column',
    'bad_operator',
    'bad_value',
    'bad_cursor',
    'not_filterable',
    'no_calendar',
    'bad_world_date',
];
/** A JSON value with every object's keys in sorted order, arrays in their own order. */
function sortedKeys(value) {
    if (Array.isArray(value))
        return value.map(sortedKeys);
    if (value === null || typeof value !== 'object')
        return value;
    const out = {};
    for (const key of Object.keys(value).sort()) {
        const item = value[key];
        if (item !== undefined)
            out[key] = sortedKeys(item);
    }
    return out;
}
/**
 * The key of one catalogue query of one type (CS r2c; 1.U deliverable 4's `canonicalQueryKey`,
 * DECISIONS 141 item 3): the query parsed with its defaults applied and `cursor` left out, object
 * keys sorted at every depth, array order (filters, sort keys, `in` values) kept, and the type key
 * part of the string. So a query, its JSON or URL round trip, any key order and its defaults
 * written out give one key, and a cursor (`{o, k}` with `k` from this key, 1.U deliverable 5) binds
 * one type and one query. It takes the query parsed or as sent (defaults not yet applied). Pure; it
 * throws only on a value that is not a `CatalogueQueryV1`.
 */
export function canonicalCatalogueQuery(typeKey, query) {
    const { cursor: _cursor, ...rest } = CatalogueQueryV1.parse(query);
    return JSON.stringify([typeKey, sortedKeys(rest)]);
}
/** A catalogue row: the document, and each requested column's values (`field_value` rows). */
export const CatalogueRowV1 = z.strictObject({
    document: DocRefV1,
    canonStatus: CanonStatus,
    updatedAt: IsoTimestamp,
    values: z.record(CatalogueColumn, z.array(JsonValue)),
});
/** `catalogue.query`'s answer: a page of rows, and how many match in all. */
export const CatalogueRowsV1 = z.strictObject({
    ...listOf(CatalogueRowV1).shape,
    total: Count,
});
// ------------------------------------------------------------------------------------ Inbox
/** `inbox.list`: every category, or one, paged; dismissed items are left out. */
export const InboxQueryV1 = z.strictObject({
    ...PageQueryV1.shape,
    category: InboxCategoryKey.optional(),
});
/** A page of the Inbox. */
export const InboxPageV1 = listOf(InboxItemV1);
/** Open items per category: the data of the `inbox.counts` event. */
export const InboxCountsV1 = InboxCountsEvent.shape.data;
/** `inbox.dismiss`: ignore one item, this once or for good (`inbox_dismissal`). */
export const InboxDismissRequestV1 = z.strictObject({
    category: InboxCategoryKey,
    subjectId: Uuid,
    scope: InboxDismissalScope,
});
/** `inbox.capture` (Mod-Shift-K): a one-line note to the Inbox, stored as a `capture`. */
export const InboxCaptureRequestV1 = z.strictObject({ text: InboxCaptureText });
// ------------------------------------------------------------------------------------ merge
/** `hygiene.merge` and its preview: merge the document of the path into the survivor. */
export const MergeRequestV1 = z.strictObject({ survivorId: Uuid, baseMetaVersion: RowVersion });
/** What happens to the loser's primary link (baseline section 3, Merge, step 2). */
export const MERGE_PRIMARY_OUTCOMES = ['none', 'demoted', 'repointed'];
/** What a merge would do (baseline section 3, Merge), counted; nothing is written. */
export const MergePreviewV1 = z.strictObject({
    loser: DocRefV1,
    survivor: DocRefV1,
    primary: z.enum(MERGE_PRIMARY_OUTCOMES),
    linksRepointed: Count,
    /** The loser's links to the survivor (or from it), which would become self-links. */
    selfLinksDropped: z.array(Uuid),
    /** Placements, states, records, sheets, mentions and every other reference. */
    referencesRepointed: Count,
    /** The loser's title and aliases the survivor gains. */
    aliasesAdded: z.array(DocTitle),
    /**
     * The loser's top-level blocks appended to the survivor with their ids, so secrets and anchors
     * follow (CS r2c, item M1; DECISIONS 144 1.N-3 item 1).
     */
    blocksMoved: Count,
});
// --------------------------------------------------------------------------------- Link all
/** `hygiene.linkAllPreview`: every unlinked occurrence that Link all would link, at most 500. */
export const LinkAllPreviewV1 = z.strictObject({
    occurrences: z.array(UnlinkedOccurrenceV1).max(500),
    total: Count,
});
/** `hygiene.linkAll`: the occurrences to link, each as `mentions.link` names one. */
export const LinkAllRequestV1 = z.strictObject({
    occurrences: z.array(MentionLinkRequestV1).min(1).max(500),
});
/** How many were linked, and each changed source's new body version. */
export const LinkAllResultV1 = z.strictObject({
    linked: Count,
    sourceBodyVersions: z.record(Uuid, RowVersion),
});
//# sourceMappingURL=hygiene.js.map